import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Button,
} from "reactstrap"
import { Select, Switch } from "antd"
import { fetchData } from "utils/Common_api_functions"

const { Option } = Select

const ProductModal = ({
  isOpen,
  toggle,
  productData: initialData = {},
  modalType = "", // "add" | "edit" | "view"
  onSave,
}) => {
  const [formData, setFormData] = useState({})
  const [suppliers, setSuppliers] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  // Fetch active suppliers
  const fetchAllSuppliers = async () => {
    setLoading(true)
    try {
      const response = await fetchData(setLoading, "supplier")
      if (response?.success) {
        const activeSuppliers = response.data
          .filter(item => item.isActive)
          .reverse()
        setSuppliers(activeSuppliers)
      }
    } catch (error) {
      console.error("Failed to load suppliers:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAllSuppliers()
  }, [])

  // Sync form data when modal opens / initial data changes
  useEffect(() => {
    if (isOpen) {
      const supplierId =
        initialData.supplier?.id || initialData.supplierId || undefined

      setFormData({
        productName: "",
        price: "",
        measuringUnit: "",
        supplierId,
        status: true,
        ischargable: false,
        ...initialData,
      })
      setErrors({})
    }
  }, [isOpen, initialData])

  const isViewMode = modalType === "view"
  const isEditMode = modalType === "edit"
  const isAddMode = modalType === "add" || !initialData?.id

  const handleChange = e => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))

    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }))
    }
  }

  const handleSupplierChange = value => {
    setFormData(prev => ({ ...prev, supplierId: value }))

    // Clear supplier error when user selects something
    if (errors.supplierId) {
      setErrors(prev => ({ ...prev, supplierId: "" }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    if (!formData.productName?.trim()) {
      newErrors.productName = "Product name is required"
    }

    if (!formData.price) {
      newErrors.price = "Price is required"
    } else if (isNaN(formData.price) || Number(formData.price) < 0) {
      newErrors.price = "Enter a valid price"
    }

    if (!formData.measuringUnit?.trim()) {
      newErrors.measuringUnit = "Measuring unit is required"
    }

    // ← NEW: Supplier is now required
    if (!formData.supplierId) {
      newErrors.supplierId = "Please select a supplier"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = () => {
    if (isViewMode) {
      toggle()
      return
    }

    if (validateForm()) {
      const dataToSend = {
        ...formData,
        price: Number(formData.price),
        status: !!formData.status,
        // Most APIs expect supplierId directly
        supplierId: formData.supplierId,
        // If your backend expects full supplier object instead, uncomment:
        // supplier: suppliers.find(s => s.id === formData.supplierId) || null,
      }

      onSave(dataToSend)
    }
  }

  const title = isViewMode
    ? "View Product"
    : isEditMode
    ? "Edit Product"
    : "Add New Product"

  const selectedSupplier = suppliers.find(
    s => s.id === formData.supplierId
  )?.supplierName

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>{title}</ModalHeader>

      <ModalBody>
        {loading && (
          <div className="text-center py-3">Loading suppliers...</div>
        )}

        <div className="row">
          {/* Product Name */}
          <div className="col-md-12 mb-3">
            <Label>
              Product Name <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="productName"
              value={formData.productName || ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. Floor Cleaner"
              invalid={!!errors.productName}
            />
            {errors.productName && (
              <small className="text-danger">{errors.productName}</small>
            )}
          </div>

          {/* Price */}
          <div className="col-md-6 mb-3">
            <Label>
              Total Price <span className="text-danger">*</span>
            </Label>
            <Input
              type="number"
              name="price"
              value={formData.price ?? ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="0.00"
              min="0"
              step="0.01"
              invalid={!!errors.price}
            />
            {errors.price && (
              <small className="text-danger">{errors.price}</small>
            )}
          </div>

          {/* Measuring Unit */}
          <div className="col-md-6 mb-3">
            <Label>
              Measuring Unit <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="measuringUnit"
              value={formData.measuringUnit || ""}
              onChange={handleChange}
              disabled={isViewMode}
              placeholder="e.g. Bottle / Pack / Box"
              invalid={!!errors.measuringUnit}
            />
            {errors.measuringUnit && (
              <small className="text-danger">{errors.measuringUnit}</small>
            )}
          </div>

          {/* Supplier Dropdown – now required */}
          <div className="col-md-12 mb-3">
            <Label>
              Supplier <span className="text-danger">*</span>
            </Label>

            {isViewMode ? (
              <div className="form-control-plaintext">
                {selectedSupplier || "—"}
              </div>
            ) : (
              <>
                <Select
                  placeholder="Select supplier"
                  value={formData.supplierId}
                  onChange={handleSupplierChange}
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                  loading={loading}
                  disabled={loading || suppliers.length === 0}
                >
                  {suppliers.map(supplier => (
                    <Option key={supplier.id} value={supplier.id}>
                      {supplier.supplierCode} - {supplier.supplierName}
                    </Option>
                  ))}
                </Select>

                {errors.supplierId && (
                  <small className="text-danger d-block mt-2">
                    {errors.supplierId}
                  </small>
                )}

                {suppliers.length === 0 && !loading && (
                  <small className="text-muted d-block mt-1">
                    No active suppliers found. Please add a supplier first.
                  </small>
                )}
              </>
            )}
          </div>
        </div>
      </ModalBody>

      <ModalFooter>
        {isViewMode ? (
          <Button color="secondary" onClick={toggle}>
            Close
          </Button>
        ) : (
          <>
            <Button color="secondary" onClick={toggle} disabled={loading}>
              Cancel
            </Button>
            <Button color="primary" onClick={handleSubmit} disabled={loading}>
              {isAddMode ? "Create Product" : "Update Product"}
            </Button>
          </>
        )}
      </ModalFooter>
    </Modal>
  )
}

export default ProductModal
